import { useState, useRef, useCallback } from 'react';
import { X, Upload, GripVertical, Loader2, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

export interface ProductImage {
  url: string;
  path: string;
  alt?: string;
}

interface ProductImageUploadProps {
  producerId: string;
  productId: string | null;
  images: ProductImage[];
  onChange: (images: ProductImage[]) => void;
  disabled?: boolean;
  /**
   * Callback to ensure a draft product exists before uploading.
   * Returns the productId or null if failed.
   */
  ensureDraftExists?: () => Promise<string | null>;
  isCreatingDraft?: boolean;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const BUCKET_NAME = 'producer-products';

export function ProductImageUpload({
  producerId,
  productId: initialProductId,
  images,
  onChange,
  disabled = false,
  ensureDraftExists,
  isCreatingDraft = false,
}: ProductImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [currentProductId, setCurrentProductId] = useState<string | null>(initialProductId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update currentProductId when prop changes (e.g., after draft creation)
  if (initialProductId && initialProductId !== currentProductId) {
    setCurrentProductId(initialProductId);
  }

  const generateStoragePath = (prodId: string, filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase() || 'jpg';
    const uuid = crypto.randomUUID();
    return `${producerId}/${prodId}/${uuid}.${ext}`;
  };

  const uploadFile = async (prodId: string, file: File): Promise<ProductImage | null> => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      throw new Error(`Ogiltigt filformat: ${file.name}. Endast JPG, PNG och WebP tillåts.`);
    }

    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`Filen ${file.name} är för stor. Max 5 MB.`);
    }

    // Compress before upload
    const { compressImageFile } = await import('@/lib/media/compressImage');
    const compressed = await compressImageFile(file);

    const path = generateStoragePath(prodId, compressed.name);

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(path, compressed, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Kunde inte ladda upp ${file.name}: ${uploadError.message}`);
    }

    const { data: publicUrlData } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(path);

    return {
      url: publicUrlData.publicUrl,
      path,
      alt: file.name.replace(/\.[^/.]+$/, ''),
    };
  };

  const handleUpload = async (files: FileList | File[]) => {
    if (disabled || uploading || isCreatingDraft) return;

    setUploadError(null);
    setUploading(true);

    try {
      // Ensure we have a productId (create draft if needed)
      let prodId = currentProductId;
      
      if (!prodId && ensureDraftExists) {
        prodId = await ensureDraftExists();
        if (prodId) {
          setCurrentProductId(prodId);
        }
      }

      if (!prodId) {
        setUploadError('Kunde inte skapa produkt för bilduppladdning.');
        setUploading(false);
        return;
      }

      const newImages: ProductImage[] = [];
      const errors: string[] = [];

      for (const file of Array.from(files)) {
        try {
          const img = await uploadFile(prodId, file);
          if (img) newImages.push(img);
        } catch (err) {
          errors.push(err instanceof Error ? err.message : 'Okänt fel');
        }
      }

      if (newImages.length > 0) {
        const updatedImages = [...images, ...newImages];
        onChange(updatedImages);
      }

      if (errors.length > 0) {
        setUploadError(errors.join(' '));
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err instanceof Error ? err.message : 'Okänt fel vid uppladdning');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (index: number) => {
    if (disabled) return;

    const image = images[index];
    if (!image) return;

    // Remove from storage
    try {
      await supabase.storage.from(BUCKET_NAME).remove([image.path]);
    } catch (err) {
      console.warn('Could not delete image from storage:', err);
    }

    // Remove from array
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files);
      }
    },
    [images, onChange, disabled, uploading, currentProductId],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !uploading && !isCreatingDraft) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Reordering via drag within gallery
  const handleItemDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleItemDragOver = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const reordered = [...images];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(index, 0, moved);
    setDraggedIndex(index);
    onChange(reordered);
  };

  const handleItemDragEnd = async () => {
    setDraggedIndex(null);
  };

  const isLoading = uploading || isCreatingDraft;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Produktbilder</CardTitle>
        <CardDescription>
          Bilder som visas för produkten i Farmly och i butiken.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
            dragOver && !disabled
              ? 'border-primary bg-primary/5'
              : 'border-muted-foreground/25 hover:border-muted-foreground/50',
            disabled && 'opacity-50 pointer-events-none',
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES.join(',')}
            multiple
            onChange={handleFileInputChange}
            className="hidden"
            disabled={disabled || isLoading}
          />

          <div className="flex flex-col items-center gap-2">
            {isLoading ? (
              <>
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {isCreatingDraft ? 'Skapar utkast...' : 'Laddar upp...'}
                </p>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Dra och släpp bilder här, eller
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled}
                >
                  Välj filer
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG eller WebP. Max 5 MB per bild.
                </p>
              </>
            )}
          </div>
        </div>

        {/* Error message */}
        {uploadError && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{uploadError}</span>
          </div>
        )}

        {/* Image gallery */}
        {images.length > 0 && (
          <TooltipProvider>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {images.map((image, index) => (
                <div
                  key={image.path}
                  draggable={!disabled}
                  onDragStart={(e) => handleItemDragStart(e, index)}
                  onDragOver={(e) => handleItemDragOver(e, index)}
                  onDragEnd={handleItemDragEnd}
                  className={cn(
                    'relative group aspect-square rounded-md border bg-muted overflow-hidden cursor-grab',
                    draggedIndex === index && 'opacity-50',
                    disabled && 'cursor-default',
                  )}
                >
                  {/* Image */}
                  <img
                    src={image.url}
                    alt={image.alt || 'Produktbild'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <GripVertical className="h-5 w-5 text-white" />
                  </div>

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={disabled}
                    className="absolute top-1 right-1 p-1 rounded-full bg-background/80 hover:bg-destructive hover:text-destructive-foreground transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Ta bort bild"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>

                  {/* Main image badge */}
                  {index === 0 && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-primary text-primary-foreground">
                          Huvudbild
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        Första bilden används som huvudbild
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
          </TooltipProvider>
        )}

        {/* Empty state */}
        {images.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
            <ImageIcon className="h-10 w-10 mb-2 opacity-50" />
            <p className="text-sm">Inga bilder ännu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
